import React from "react";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import { clashesWithOthers, toDisplayDate } from "../services/timings";
import DateField from "../DateField";
import EmphasizedMessage from "../EmphasizedMessage";
import TimeSelect from "./TimeSelect";
import { WEEK_DAYS, dayLabel, durationOptionsFor } from "./constants";
import styles from "./SchedulePicker.module.css";

/**
 * The Schedule section body: start date + class duration, the "Add schedule"
 * day chips, one time row per added class, the projected completion, the
 * completion-exceeds-expiry warning and the find-mentors CTA.
 *
 * Rows are keyed by their own `id`, not by weekday: a weekday with two classes
 * a week is two rows (R-04-04), so an added day's chip adds another time for it.
 *
 * @param {{ schedule: {startDate: string, duration: string,
 *             days: {id: string, day: string, time: string|null}[]},
 *           handlers: {onStartDateChange: (value: string) => void,
 *             onDurationChange: (value: string) => void,
 *             onAddDay: (day: string) => void, onRemoveDay: (id: string) => void,
 *             onTimeChange: (id: string, time: string) => void},
 *           constraints?: {minStartDate?: string, isStartReadOnly?: boolean,
 *             areDaysReadOnly?: boolean, showPastStartError?: boolean,
 *             startDateError?: string},
 *           completion?: {projected?: string|null,
 *             overrun?: {completionDate: string, expiryDate: string}|null}|null,
 *           children?: React.ReactNode }} props
 */
const SchedulePicker = ({
  schedule,
  handlers,
  constraints = {},
  completion = null,
  children,
}) => {
  const { startDate, duration, days } = schedule ?? {};
  const {
    minStartDate,
    isStartReadOnly,
    areDaysReadOnly,
    showPastStartError = true,
    startDateError = "",
  } = constraints;
  const isStartTooEarly =
    showPastStartError && !isStartReadOnly && minStartDate && startDate && startDate < minStartDate;
  /* `min` only limits the native picker; a typed date can still be earlier. The
     earliest start is today, so an earlier one is in the past, and the message says so. */
  const startMessage = isStartTooEarly
    ? `The start date ${toDisplayDate(startDate)} is in the past. Choose ${toDisplayDate(minStartDate)} or a later date.`
    : startDateError;

  return (
    <div className={styles.wrap}>
      {children}

      <div className={styles.fieldGrid}>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Start date</span>
          <DateField
            value={startDate}
            min={minStartDate}
            disabled={isStartReadOnly}
            invalid={!!startMessage}
            ariaLabel="Start date"
            onChange={handlers?.onStartDateChange}
          />
        </label>
        {/* Under the start date, but across the whole row: in the date's own column
            a one-line message wrapped to three. */}
        {!!startMessage && (
          <span className={styles.fieldError} role="alert">
            <EmphasizedMessage text={startMessage} />
          </span>
        )}
        <label className={styles.field}>
          <span className={styles.fieldLabel}>Class duration</span>
          <select
            className={styles.input}
            value={duration ?? ""}
            disabled={areDaysReadOnly}
            onChange={(event) => handlers?.onDurationChange?.(event?.target?.value)}
          >
            <option value="" disabled>
              Select duration
            </option>
            {durationOptionsFor(duration)?.map((option) => (
              <option key={option?.value} value={option?.value}>
                {option?.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className={styles.addSchedule}>
      <div className={styles.fieldLabel}>Add schedule</div>
      <div className={styles.dayChips}>
        {WEEK_DAYS?.map((weekDay) => (
          <button
            key={weekDay?.value}
            type="button"
            className={styles.dayChip}
            disabled={areDaysReadOnly}
            onClick={() => handlers?.onAddDay?.(weekDay?.value)}
          >
            <SvgIcons name="circular_plus_icon" width="16" height="16" />
            {weekDay?.text?.slice(0, 3)}
          </button>
        ))}
      </div>
      </div>

      {days?.length > 0 && (
        <div className={styles.dayRows}>
          {days?.map((entry) => (
            <div key={entry?.id} className={styles.dayRow}>
              <span className={styles.dayRowName}>{dayLabel(entry?.day)}</span>
              <span className={styles.timeField}>
                <TimeSelect
                  value={entry?.time}
                  disabled={areDaysReadOnly}
                  onChange={(time) => handlers?.onTimeChange?.(entry?.id, time)}
                  clashes={(time) =>
                    clashesWithOthers(
                      { day: entry?.day, time },
                      days?.filter((other) => other?.id !== entry?.id),
                      duration,
                    )
                  }
                />
              </span>
              <button
                type="button"
                className={styles.removeDay}
                title={`Remove ${dayLabel(entry?.day)}`}
                disabled={areDaysReadOnly}
                onClick={() => handlers?.onRemoveDay?.(entry?.id)}
              >
                <SvgIcons name="delete" width="24" height="24" />
              </button>
            </div>
          ))}
        </div>
      )}

      {completion?.projected && !completion?.overrun && (
        <div className={styles.projected}>
          Projected batch completion: <strong>{completion?.projected}</strong>
        </div>
      )}

      {/* A warning, not a blocker: the backend asks for it to be confirmed at save
          (validity-overrun), so it is yellow here and never listed as a reason the
          button is disabled. */}
      {completion?.overrun && (
        <div className={styles.warningBanner} role="status">
          <SvgIcons
            name="exclamation_circle_filled"
            className={styles.warningIcon}
            width="18"
            height="18"
          />
          <div>
            <div>
              Projected batch completion: <strong>{completion?.overrun?.completionDate}</strong>
              {" │ "}Course expiry date: <strong>{completion?.overrun?.expiryDate}</strong>
            </div>
            <div>
              Projected batch completion date exceeds course expiry date. Increase the class
              frequency to complete the course before it expires.
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchedulePicker;
